import type { MouseEvent, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { SelectDropdown } from 'components/SelectDropdown';
import { SelectTrigger } from 'components/SelectTrigger';
import { selectDropdownContentVariants, selectSurfaceVariants } from './selectVariants';
import { SelectSearchInput } from './SelectSearchInput';
import { SelectCloseContext, SelectOpenContext } from './SelectContext';

interface SelectSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export interface SelectProps {
  label: ReactNode;
  value?: ReactNode;
  selectedCount?: number;
  hasValue: boolean;
  children: ReactNode;
  footer?: ReactNode;
  search?: SelectSearchProps;
  mode?: 'flat' | 'tree';
  disabled?: boolean;
  id?: string;
  className?: string;
  border?: 'default' | 'transparent';
  size?: 'default' | 'small';
  dropdownMinWidth?: number;
  dropdownFitContent?: boolean;
  fullWidth?: boolean;
  showLabel?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export function Select({
  label,
  value = '',
  selectedCount = undefined,
  hasValue,
  children,
  footer = undefined,
  search = undefined,
  mode = 'flat',
  disabled = false,
  id = undefined,
  className = undefined,
  border = 'default',
  size = 'default',
  dropdownMinWidth = 270,
  dropdownFitContent = false,
  fullWidth = false,
  showLabel = true,
  onOpen = undefined,
  onClose = undefined,
}: SelectProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      onOpen?.();
    },
    [onOpen]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    onClose?.();
  }, [onClose]);

  return (
    <SelectCloseContext.Provider value={handleClose}>
      <SelectOpenContext.Provider value={open}>
        <div className={selectSurfaceVariants({ width: fullWidth ? 'full' : 'content' })}>
          <SelectTrigger
            label={label}
            value={value}
            selectedCount={selectedCount}
            hasValue={hasValue}
            open={open}
            disabled={disabled}
            id={id}
            className={className}
            border={border}
            size={size}
            fullWidth={fullWidth}
            showLabel={showLabel}
            onClick={handleOpen}
          />
          <SelectDropdown
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            mode={mode}
            id={id}
            minWidth={dropdownMinWidth}
            fitContent={dropdownFitContent}
          >
            <div className={selectDropdownContentVariants({ mode })}>
              {search && (
                <SelectSearchInput
                  value={search.value}
                  onChange={search.onChange}
                  placeholder={search.placeholder}
                  autoFocus={search.autoFocus ?? true}
                  open={open}
                  mode={mode}
                />
              )}
              <div
                className="min-h-0 flex-1 overflow-y-auto"
                role="listbox"
                aria-label={typeof label === 'string' ? label : undefined}
              >
                {children}
              </div>
              {footer}
            </div>
          </SelectDropdown>
        </div>
      </SelectOpenContext.Provider>
    </SelectCloseContext.Provider>
  );
}
